/**
 * @copyright 2026 Fortee Uniforme Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying prohibited.
 * Protected under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 */

// fortee-logo-editor.js - shared logo editor (Fabric.js canvas, text, shapes,
// effects, undo/redo, garment preview, scope) used by BOTH builders.
//
// Extracted verbatim from bundle-builder-2step.js:2323-3077 (Plan 01). Method
// bodies are UNCHANGED so the 2-step keeps behaving identically when it is
// switched over in Plan 02.
//
// Usage - spread both into the Alpine component:
//     Object.assign(component, window.forteeLogoEditorState(), window.forteeLogoEditor());
//
// ADAPTER CONTRACT - the host builder MUST provide these; the editor calls only
// these to reach builder-specific artwork state:
//
//   _activeArtworkContext(productId, view) -> { product, color, artwork } | null
//   _activeViewForProduct(productId)       -> view key
//   _autoVersionKey(product, color, art)   -> version key string
//   _artworkUrlFromObj(artwork)            -> url string
//   _buildPrePlacedTiles()                 -> re-render mockups after a save
//   _generateVersions(...)                 -> build the logo colour versions
//   step2_getColorHex(product, color)      -> garment hex (backdrop + tone)
//   step2_getColors(product)               -> colour names for the garment chips
//   triggerUpload()                        -> start the upload-new flow
//   showToast(msg, ok)                     -> user feedback
//   _t(key, fallback)                      -> i18n lookup
//   groupArtwork / colorDirective / editorBg -> per-view artwork stores
//   _whiteOutlineOnDark, _mockup2Cache
//
// The list above is the COMPLETE set (16) and was derived by scanning both
// `this.X` and `self.X` — an earlier scan checked only `this.X` and so missed
// _generateVersions and step2_getColors.
//
// A host must ALSO expose Alpine.store('builder').bundleProducts for the garment
// chips, since editorGarmentChips() reads it to build the background swatches.
//
// NOTE - these five are defined here but ALSO called from outside the editor by
// the 2-step (mockups / review). They resolve through `this` once spread in, so
// no shim is needed: _lineDirective, _openScope, _swapBaseLogo, _toneForHex,
// _versionKeyForTone.

(function () {
  'use strict';

  // Scratch artwork slot an "Upload a new logo" lands in before the customer
  // picks a scope. It is never a placement: never a scope target, never saved.
  var NEW_LOGO_KEY = '__newlogo__';

  // Editor-owned reactive state. Kept separate from the methods so a host can
  // merge it without hand-declaring ~38 fields.
  window.forteeLogoEditorState = function forteeLogoEditorState() {
    return {
      // Editor palettes. These were component state in bundle-builder-2step.js
      // (lines 233-239), ABOVE the extracted range, so they never came across —
      // yet the shared snippet iterates them:
      //     x-for="f in TEXT_FONTS"      (font dropdown)
      //     x-for="col in SWATCH_COLORS" (text/shape colour swatches)
      // Without them both lists rendered EMPTY on the single builder. They belong
      // to the editor, so they live with it now.
      SWATCH_COLORS: ['#ffffff', '#16181b', '#c8322b', '#2b3a55', '#d9a441'],
      TEXT_FONTS: [
        { label: 'Archivo Black', css: "'Archivo Black', sans-serif" },
        { label: 'Inter', css: "'Inter', sans-serif" },
        { label: 'Georgia', css: 'Georgia, serif' },
        { label: 'Courier Prime', css: "'Courier Prime', monospace" }
      ],
      _editorOpen: false,                       // logo editor drawer (single-screen MVP)
      _editorView: null,                        // which placement's view the editor targets
      _editorVersionKey: 'original',            // resolved preview version for the current garment
      _editorGarmentBg: null,
      _fabricProductId: null,                   // product whose logo is being edited
      _selHasObj: false,
      _selType: '',                             // 'text' | 'shape' | 'image'
      _edSeg: 'background',                      // mobile ≤767 segment: background|colors|add|style
      _edSegPrev: 'background',                  // last content segment, restored when leaving Style
      _canUndo: false, _canRedo: false,
      _histT: null,                             // debounce handle for slider-driven history snapshots
      _shapeTilesOpen: false,                   // ＋ Shape reveals the 4 shape tiles
      _shpFill: '#16181b',                      // shape panel — synced from the active shape
      _shpOutlineOnly: false,
      _shpThickness: 3,
      _txtColor: '#16181b',
      _txtCurve: 0,                             // -100..100 → arc text-on-path (0 = straight)
      _txtEffect: 'none',                       // none|outline|hollow|shadow (Fix Pack 05 P1)
      _txtFont: "'Archivo Black', sans-serif",  // text panel — synced from the active text object
      _txtFxColor: '',                          // effect colour override; '' = automatic
      _txtSize: 36,
      _txtSpacing: 0,                           // letter spacing -> Fabric charSpacing (1/1000 em)
      // Tunable effect parameters. Previously every effect value was DERIVED from
      // font size with no way to adjust it. These are seeded from those same
      // derived values whenever an effect is picked, so presets look exactly as
      // before until the customer moves a slider.
      _fxWidth: 3,        // outline / hollow stroke thickness (px)
      _fxAngle: 90,       // shadow direction, degrees clockwise from 3 o'clock
      _fxDistance: 3,     // shadow offset distance (px)
      _fxBlur: 4,         // shadow blur radius (px)
      _scopeOpen: false,                        // "where should this apply?" modal open
      _scopeChoice: 'front',                    // all | front | back | cap
      _scopePendingUrl: null,                   // edited/new logo awaiting a scope decision
      _scopePendingEditorJSON: null,            // canvas.toJSON() of the edited composition, applied to every scoped view
      _scopePendingSource: null,                // NEW UPLOAD: the whole uploaded artwork record (raw original + cutout); null for an editor save
      _scopePendingEdits: null,                 // editor save: { editsLayerUrl, editsSpec } for the edits-only copy, or null
      _pendingScopeProductId: null,
      _pendingUploadIsNew: false,               // next upload is a "new logo" (routes to scope)
      _uploadNewProductId: null,
      _artworkEditFor: null,                    // productId whose Artwork editor modal is open, or null
      _artColorsDraft: [],                      // brand-colour hex list being edited in the panel
      _artNoteDraft: '',                        // textarea model for the instructions panel
      _artConfirm: false,                       // inline "Noted on your order" confirmation visible
      _editorFontsLoaded: false,                // one-shot guard: Google Fonts load once, on drawer open
      _edMqlBound: false,
      _outlineMinHold: false,                   // keeps the editor "Preparing outline…" indicator up for a brief minimum so the feedback is always perceptible (even when the build is fast or cached).
    };
  };

  window.forteeLogoEditor = function forteeLogoEditor() {
    return {
      openEditor: function (productId) {
        this._artworkEditFor = null;
        this._fabricProductId = productId;
        if (!this._editorView) this._editorView = this._activeViewForProduct(productId);
        this._ensureEditorState(this._editorView);
        // Reflect any saved directive so reopening the editor restores the panel/confirmation.
        var _d = this.colorDirective[this._editorView];
        this._artNoteDraft = (_d && _d.note) || '';
        this._artColorsDraft = (_d && _d.colors) ? _d.colors.slice() : [];
        this._artConfirm = !!(_d && _d.mode === 'art_instructions' && (_d.note || (_d.colors && _d.colors.length)));
        // Resolve which prepared version is the base logo for this garment/view.
        var _ec = this._activeArtworkContext(productId, this._editorView);
        var _ek = 'original';
        if (_ec && _ec.artwork && _ec.artwork.versionUrls) {
          _ek = this._autoVersionKey(_ec.product, _ec.color, _ec.artwork);
        }
        this._editorVersionKey = _ek || 'original';
        // Default garment-preview backdrop = the garment the customer came from.
        this._editorGarmentBg = (_ec && _ec.product && _ec.color)
          ? (this.step2_getColorHex(_ec.product, _ec.color) || null) : null;
        this._selHasObj = false; this._selType = '';
        // MVP: the drawer has ONE mode — the canvas is always live while open.
        this._editorOpen = true;
        var self = this;
        this.$nextTick(function () { self._initEditorCanvas(); });
      },
      closeEditor: function () {
        this._editorOpen = false;
        this._disposeCanvas();
      },
      // Resolve the base-logo URL for the current preview version (garment tone).
      editorCurrentUrl: function () {
        var c = this._activeArtworkContext(this._fabricProductId, this._editorView);
        var urls = c && c.artwork ? c.artwork.versionUrls : null;
        if (urls && urls[this._editorVersionKey]) return urls[this._editorVersionKey];
        return c && c.artwork ? this._artworkUrlFromObj(c.artwork) : null;
      },
      _disposeCanvas: function () {
        var canvas = window.__ftb2FabricCanvas;
        if (canvas) { try { canvas.dispose(); } catch (e) {} window.__ftb2FabricCanvas = null; }
        window.__ftb2BaseLogo = null;
        window.__ftb2Hist = null;
      },
      // MVP canvas: lazy Fabric via ensureFabric, locked base logo, fit-to-width once.
      // The live instance lives on window.__ftb2FabricCanvas (NOT reactive Alpine state —
      // Alpine's proxy corrupts Fabric internals). Base handle + history on window.__ftb2*.
      _initEditorCanvas: function () {
        var self = this;
        if (!window.forteeShared || !window.forteeShared.ensureFabric) return;
        // Drawer is opening — lazy-load the editor fonts alongside Fabric (ACC-09:
        // nothing font-related loads until now).
        self._loadEditorFonts();
        var url = self.editorCurrentUrl();
        self._disposeCanvas();
        self._canUndo = false; self._canRedo = false;
        self._selHasObj = false; self._selType = '';
        self._edSeg = 'background'; self._edSegPrev = 'background';   // fresh segment each open
        // Re-fit the canvas when crossing the 768px breakpoint while the editor is open (bind once).
        if (!self._edMqlBound && window.matchMedia) {
          self._edMqlBound = true;
          try {
            window.matchMedia('(min-width: 768px)').addEventListener('change', function () {
              if (self._editorOpen) self._refitEditorCanvas();
            });
          } catch (e) {}
        }
        // Lazy-load Fabric.js — never requested until the drawer opens (ACC-09).
        window.forteeShared.ensureFabric().then(function () {
          var host = document.getElementById('ftb2-fabric-host');
          var wrap = document.querySelector('.ftb2-ed2-canvas-wrap');
          if (!host || typeof fabric === 'undefined') return;
          host.innerHTML = '';
          // Fit the canvas to the container width ONCE (fit-only — no zoom controls).
          var avail = (wrap && wrap.clientWidth) || host.clientWidth || 480;
          var w = Math.max(300, Math.min(560, avail - 36));
          var h = Math.round(w * 0.732);
          var el = document.createElement('canvas');
          host.appendChild(el);
          var canvas = new fabric.Canvas(el, { width: w, height: h, selection: true, preserveObjectStacking: true });
          window.__ftb2FabricCanvas = canvas;
          // Blue selection handles (per MOCKUP); prototype defaults are idempotent.
          try {
            fabric.Object.prototype.transparentCorners = false;
            fabric.Object.prototype.cornerColor = '#2563eb';
            fabric.Object.prototype.cornerStrokeColor = '#ffffff';
            fabric.Object.prototype.cornerSize = 11;
            fabric.Object.prototype.borderColor = '#2563eb';
            fabric.Object.prototype.padding = 4;
          } catch (e) {}
          // Selection drives the right panel: empty-state / text panel / shape panel (36-03).
          canvas.on('selection:created', function () { self.showPanelFor(); });
          canvas.on('selection:updated', function () { self.showPanelFor(); });
          canvas.on('selection:cleared', function () { self.showPanelFor(null); });
          // add / remove / modify push a history snapshot; text edits snapshot debounced.
          canvas.on('object:added', function () { self._pushHistory(); });
          canvas.on('object:removed', function () { self._pushHistory(); });
          canvas.on('object:modified', function () { self._pushHistory(); });
          canvas.on('text:changed', function () { self._recurveActive(); });
          self._buildEditorScene(canvas, url);
        });
      },
      // Populate the freshly-built canvas: either rehydrate a stored composition
      // (reopen → editable objects, ACC-03) or drop in the locked base logo. Setup
      // runs under the restoring flag so it yields exactly ONE baseline snapshot.
      _buildEditorScene: function (canvas, url) {
        var self = this;
        window.__ftb2Hist = { stack: [], idx: -1, restoring: true };
        var seed = function () {
          var H = window.__ftb2Hist; if (!H) return;
          H.restoring = false;
          self._pushHistory();          // one clean baseline snapshot
        };
        var art = self._editorView ? self.groupArtwork[self._editorView] : null;
        var reopenJSON = art && art.editorJSON ? art.editorJSON : null;
        if (reopenJSON) {
          canvas.loadFromJSON(JSON.parse(reopenJSON)).then(function () {
            if (window.__ftb2FabricCanvas !== canvas) return;
            var base = canvas.getObjects().find(function (o) { return o.isBase; }) || null;
            if (base) { base.set({ selectable: false, evented: false }); canvas.sendObjectToBack(base); }
            window.__ftb2BaseLogo = base;
            canvas.requestRenderAll();
            seed();
          }).catch(function () { seed(); });
        } else {
          self._loadBaseLogo(canvas, url, seed);
        }
      },
      // Add the customer's logo as a LOCKED base layer: selectable:false, evented:false,
      // isBase:true, sent to back — customers move text/shapes around it, never it.
      _loadBaseLogo: function (canvas, url, done) {
        if (!url) { if (done) done(); return; }
        fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' }).then(function (img) {
          if (window.__ftb2FabricCanvas !== canvas) return; // drawer re-opened under us
          // Fit BOTH axes: scaling by width alone clipped logos taller than wide.
          var logoBox = Math.min(canvas.getWidth(), canvas.getHeight()) * 0.55;
          img.scale(Math.min(logoBox / img.width, logoBox / img.height));
          img.set({
            left: canvas.getWidth() / 2, top: canvas.getHeight() / 2,
            originX: 'center', originY: 'center',
            selectable: false, evented: false, isBase: true
          });
          canvas.add(img);
          canvas.sendObjectToBack(img);
          window.__ftb2BaseLogo = img;
          canvas.requestRenderAll();
          if (done) done();
        }).catch(function (e) {
          console.warn('[FORTTEE 2step] base logo load failed', e);
          if (done) done();
        });
      },

      // =====================================================================
      // Phase 36-02 — Background choice · Logo-colours art directive · preview note
      // =====================================================================
      // Lazily guarantee both per-view fields exist (views can be added after the
      // initial pre-init via the placement cascade) so a directive is NEVER NULL.
      _ensureEditorState: function (view) {
        if (!view) return;
        if (this.editorBg[view] == null) this.editorBg[view] = 'removed';
        if (!this.colorDirective[view]) this.colorDirective[view] = { mode: 'art_decide', note: '', colors: [] };
      },
      // Committed directive mode for the view the editor targets.
      _dirMode: function () {
        var view = this._editorView;
        return (view && this.colorDirective[view] && this.colorDirective[view].mode) || 'art_decide';
      },
      // ---- BACKGROUND radios: swap the base image, zero processing ----
      // Resolve the base-logo URL for the current Background choice. 'original'
      // shows the raw logo WITH its background; 'removed' shows the prepared
      // (recoloured-for-preview) version.
      _editorBaseUrl: function () {
        var c = this._activeArtworkContext(this._fabricProductId, this._editorView);
        if (!c || !c.artwork) return null;
        var a = c.artwork;
        if ((this.editorBg[this._editorView] || 'removed') === 'original') {
          return a.originalCroppedUrl || a.originalUrl || a.url || null;
        }
        var urls = a.versionUrls;
        if (urls && this._editorVersionKey && urls[this._editorVersionKey]) return urls[this._editorVersionKey];
        return a.bgRemovedCroppedUrl || a.bgRemovedUrl || a.url || null;
      },
      setEditorBg: function (mode) {
        var view = this._editorView; if (!view) return;
        this._ensureEditorState(view);
        this.editorBg[view] = mode;
        this._swapBaseLogo();
        this.showToast(mode === 'removed'
          ? this._t('artwork_bg_removed', 'Background removed.')
          : this._t('artwork_bg_kept', 'Original background restored.'), false);
      },
      // Replace the locked base layer in place (keeps its centre/rotation). Runs
      // under the history restoring flag so the remove+add pair yields ONE snapshot.
      _swapBaseLogo: function (skipHistory) {
        var canvas = window.__ftb2FabricCanvas;
        if (!canvas || typeof fabric === 'undefined') return;
        var url = this._editorBaseUrl(); if (!url) return;
        var self = this;
        var old = window.__ftb2BaseLogo;
        var H = window.__ftb2Hist;
        fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' }).then(function (img) {
          if (window.__ftb2FabricCanvas !== canvas) return;
          var logoBox = Math.min(canvas.getWidth(), canvas.getHeight()) * 0.55;
          var left = (old && old.left != null) ? old.left : canvas.getWidth() / 2;
          var top = (old && old.top != null) ? old.top : canvas.getHeight() / 2;
          var angle = old ? (old.angle || 0) : 0;
          img.scale(Math.min(logoBox / img.width, logoBox / img.height));
          img.set({ left: left, top: top, angle: angle, originX: 'center', originY: 'center', selectable: false, evented: false, isBase: true });
          if (H) H.restoring = true;
          if (old) canvas.remove(old);
          canvas.add(img); canvas.sendObjectToBack(img);
          window.__ftb2BaseLogo = img;
          canvas.requestRenderAll();
          if (H) H.restoring = false;
          // Garment-preview swaps pass skipHistory: changing the preview backdrop is
          // not a design edit and must not create an undo step.
          if (!skipHistory) self._pushHistory();
        }).catch(function () {});
      },
      // ---- LOGO COLORS radios: switch between art-team directive modes ----
      setArtMode: function (mode) {
        var view = this._editorView; if (!view) return;
        this._ensureEditorState(view);
        var d = this.colorDirective[view];
        d.mode = mode;
        if (mode === 'art_decide') {
          // Reset to the default directive; hide the confirmation + clear drafts.
          d.note = ''; d.colors = [];
          this._artNoteDraft = ''; this._artColorsDraft = [];
          this._artConfirm = false;
          this.showToast(this._t('artwork_art_decide', 'Our art team will pick the best colors for every garment.'), true);
        } else {
          // Opening the instructions panel — seed the drafts from any saved directive
          // and show the panel (confirmation hidden until Save).
          this._artNoteDraft = d.note || '';
          this._artColorsDraft = (d.colors || []).slice();
          this._artConfirm = !!(d.note || (d.colors && d.colors.length));
        }
      },
      // ---- instructions panel: brand-colour swatches + save ----
      addArtColor: function (hex) {
        if (!hex) return;
        hex = String(hex);
        if (this._artColorsDraft.indexOf(hex) === -1) this._artColorsDraft.push(hex);
      },
      removeArtColor: function (i) {
        if (i >= 0 && i < this._artColorsDraft.length) this._artColorsDraft.splice(i, 1);
      },
      // Record the directive on this view. The note is UNTRUSTED free text: it is
      // stored raw in state and only ever rendered back via x-text (never x-html)
      // and serialized as a Shopify line-item property (Shopify escapes it on the
      // order) — see T-36-03.
      saveArtNote: function () {
        var view = this._editorView; if (!view) return;
        this._ensureEditorState(view);
        var note = (this._artNoteDraft || '').trim();
        var colors = (this._artColorsDraft || []).slice();
        if (!note && !colors.length) {
          this.showToast(this._t('artwork_art_need_input', 'Add a note or a color so our team knows what to do.'), false);
          return;
        }
        var d = this.colorDirective[view];
        d.mode = 'art_instructions';
        d.note = note;
        d.colors = colors;
        this._artConfirm = true;
        this.showToast(this._t('artwork_art_saved', 'Instructions saved — our art team will follow them exactly.'), true);
      },
      // Saved brand-colour count for the active view (drives the confirmation copy).
      _artColorsSavedCount: function () {
        var view = this._editorView;
        var d = view && this.colorDirective[view];
        return (d && d.colors && d.colors.length) || 0;
      },
      // Aggregate the colour directive for a cart line that may span several
      // placement views. art_instructions wins if ANY view carries real
      // instructions; otherwise the line defaults to art_decide (never NULL).
      _lineDirective: function (views) {
        var self = this, mode = 'art_decide', notes = [], colors = [];
        (views || []).forEach(function (vw) {
          var d = self.colorDirective[vw];
          if (!d || d.mode !== 'art_instructions') return;
          if (!d.note && !(d.colors && d.colors.length)) return;
          mode = 'art_instructions';
          if (d.note && notes.indexOf(d.note) === -1) notes.push(d.note);
          (d.colors || []).forEach(function (col) { if (colors.indexOf(col) === -1) colors.push(col); });
        });
        return { mode: mode, note: notes.join(' · '), colors: colors };
      },
      // ---- preview-only recolor label ----
      // Show "final colors optimized by our art team" whenever the previewed base
      // is NOT the original (i.e. a white/black preview on a dark/medium garment).
      _previewIsRecolored: function () {
        if (!this._editorOpen) return false;
        if ((this.editorBg[this._editorView] || 'removed') === 'original') return false;
        return !!(this._editorVersionKey && this._editorVersionKey !== 'original');
      },

      // =====================================================================
      // Phase 36-04 — garment preview chips (one per garment colour in the order)
      // =====================================================================
      // Distinct garment colours across every product in the current bundle order
      // (not a hardcoded list): { name, hex, tone }. The transparent/checker chip
      // is rendered separately in the markup.
      editorGarmentChips: function () {
        var self = this;
        // `store` was a CLOSURE variable in bundle-builder-2step.js
        // (`var store = Alpine.store('builder')`). Extracting this method into its
        // own file broke that binding: `typeof store` became 'undefined', so the
        // chip list came back empty and the garment-background picker silently
        // vanished from the editor. Resolve the store at call time instead — by
        // the time the editor is open, Alpine is always loaded.
        var _bStore = (window.Alpine && typeof window.Alpine.store === 'function')
          ? window.Alpine.store('builder')
          : null;
        var products = (_bStore && _bStore.bundleProducts) || [];
        // Preview only the colours of the garment being edited — not every product in the
        // bundle. Fall back to all products if the edited product id is somehow unknown.
        if (self._fabricProductId != null) {
          var scoped = products.filter(function (p) { return String(p.id) === String(self._fabricProductId); });
          if (scoped.length) products = scoped;
        }
        var seen = {}, out = [];
        products.forEach(function (p) {
          // Every colour the product offers — so the customer can preview the logo on any
          // available garment colour, not only the ones already added to the order.
          var colors = self.step2_getColors(p) || [];
          colors.forEach(function (name) {
            if (!name) return;
            var hex = self.step2_getColorHex(p, name);
            var key = String(hex || '').toLowerCase();
            if (!key || seen[key]) return;
            seen[key] = true;
            out.push({ name: name, hex: hex, tone: self._toneForHex(hex) });
          });
        });
        return out;
      },
      // Coarse garment tone from a hex — light / medium / dark. Drives the preview
      // version resolver (medium+dark → a recoloured preview) and the "optimized" label.
      _toneForHex: function (hex) {
        var s = String(hex || '').trim().replace(/^#/, '');
        if (s.length === 3) s = s.split('').map(function (c) { return c + c; }).join('');
        if (s.length !== 6) return 'medium';
        var r = parseInt(s.slice(0, 2), 16), g = parseInt(s.slice(2, 4), 16), b = parseInt(s.slice(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) return 'medium';
        var lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        if (lum > 0.62) return 'light';
        if (lum < 0.32) return 'dark';
        return 'medium';
      },
      // Resolve the editor preview version key for a coarse garment tone. Mirrors
      // the mockup resolver (_autoVersionKey): the logo previews as the original
      // on every garment unless the customer opted into the white outline, in
      // which case dark chips preview the colour+white-outline version.
      _versionKeyForTone: function (tone) {
        var c = this._activeArtworkContext(this._fabricProductId, this._editorView);
        var art = c && c.artwork;
        if (!art || !art.versionUrls) return 'original';
        if (!this._whiteOutlineOnDark) return 'original';
        var binary = (tone === 'light' || tone === 'none') ? 'light' : 'dark';
        return binary === 'dark' ? 'outlineDark' : 'original';
      },
      // Tap a garment chip: set the canvas backdrop colour (CSS, behind the alpha)
      // and re-resolve the preview version so the logo reads white-on-dark etc.
      // Preview-only — reloads the base without a history entry.
      setGarment: function (hex, tone) {
        this._editorGarmentBg = hex || null;
        this._editorVersionKey = this._versionKeyForTone(tone || 'none');
        this._swapBaseLogo(true);
      },

      // Lazy editor-font loader — the three web fonts (Archivo Black, Inter, Courier
      // Prime) load ONCE, only when the drawer first opens, with Latin-Extended so
      // accented glyphs (ÉQUIPE MONTRÉAL) render. Georgia is a system serif — no load.
      // Invoked from _initEditorCanvas (drawer-open path); never referenced at page load.
      _loadEditorFonts: function () {
        if (this._editorFontsLoaded) return;
        this._editorFontsLoaded = true;
        if (!document.querySelector('link[data-ftb2-editor-fonts]')) {
          var link = document.createElement('link');
          link.rel = 'stylesheet';
          link.setAttribute('data-ftb2-editor-fonts', '');
          // Only weight 400 is ever rendered (the editor never sets fontWeight), and
          // French accents (É, Ê, à, ç…) live in the "latin" subset — so the browser
          // pulls just one small woff2 per family. display=swap avoids invisible text.
          link.href = 'https://fonts.googleapis.com/css2?family=Archivo+Black&family=Inter:wght@400&family=Courier+Prime&display=swap';
          document.head.appendChild(link);
        }
        // Kick the actual glyph downloads (with an accented sample so the latin subset
        // that carries É/Ê loads) then re-render so any added IText swaps to the real face.
        if (document.fonts && document.fonts.load) {
          var accented = 'ÉQUIPE MONTRÉAL';
          Promise.all([
            document.fonts.load('16px "Archivo Black"', accented),
            document.fonts.load('16px "Inter"', accented),
            document.fonts.load('16px "Courier Prime"', accented)
          ]).then(function () {
            var c = window.__ftb2FabricCanvas; if (c) c.requestRenderAll();
          }).catch(function () {
            var c = window.__ftb2FabricCanvas; if (c) c.requestRenderAll();
          });
        }
      },

      // ==== Phase 36-03 — MVP text authoring + swatch/outline controls ====
      // Default ink for new objects: white on a dark garment (white base version),
      // else near-black. Garment-aware chips arrive in 36-04; this keeps new text
      // visible on the current preview.
      _defaultInk: function () { return (this._editorVersionKey === 'white') ? '#ffffff' : '#16181b'; },
      _isLight: function (hex) {
        if (!hex || typeof hex !== 'string' || hex[0] !== '#' || hex.length < 7) return false;
        var r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
        return (r * 299 + g * 587 + b * 114) / 1000 > 150;
      },
      // Debounced snapshot for continuous controls (size / thickness sliders) so a
      // drag yields one history entry, not dozens (36-01 history is capped at 30).
      _pushHistoryDebounced: function () {
        var self = this; clearTimeout(this._histT);
        this._histT = setTimeout(function () { self._pushHistory(); }, 350);
      },
      // ＋ Text — a fabric.IText centered, immediately in editing mode (double-tap
      // re-edits via Fabric's built-in). Tagged objType:'text' for history + panel routing.
      addText: function () {
        var c = window.__ftb2FabricCanvas; if (!c || typeof fabric === 'undefined') return;
        var t = new fabric.IText('Your text', {
          left: c.width / 2, top: c.height - 70, originX: 'center', originY: 'center',
          fontFamily: "'Archivo Black', sans-serif", fontSize: 36, fill: this._defaultInk(), objType: 'text',
          fxEffect: 'none', fxColor: '', fxCurve: 0, charSpacing: 0
        });
        c.add(t); c.setActiveObject(t);                 // object:added → one history snapshot
        try { t.enterEditing(); if (t.selectAll) t.selectAll(); } catch (e) {}
        c.requestRenderAll(); this.showPanelFor(t);
      },
      // Apply the text panel controls (font / size / colour / effect) to the active text.
      applyText: function () {
        var c = window.__ftb2FabricCanvas; var o = c && c.getActiveObject();
        if (!o || o.objType !== 'text') return;
        var size = parseInt(this._txtSize, 10) || 36;
        o.set({ fontFamily: this._txtFont, fontSize: size, charSpacing: parseInt(this._txtSpacing, 10) || 0 });
        this._applyFx(o, this._txtEffect || 'none', this._txtColor, size);
        this._applyCurve(o, this._txtCurve);
        if (o.setCoords) o.setCoords();
        c.requestRenderAll(); this._pushHistoryDebounced();
      },
      pickTextColor: function (col) { this._txtColor = col; this.applyText(); },
      // Fix Pack 05 P1 — pick a text effect (mutually exclusive) then re-apply.
      // The values the presets always used, derived from font size. Kept as the
      // seed so switching effects reproduces the previous look exactly.
      _fxAuto: function (size) {
        size = size || 36;
        return { width: Math.max(1, size / 12), distance: Math.max(2, size * 0.08), blur: Math.max(3, size * 0.11) };
      },
      setTextEffect: function (fx) {
        this._txtEffect = fx || 'none';
        // Seed the sliders from the preset so they start where the effect is.
        var a = this._fxAuto(parseInt(this._txtSize, 10) || 36);
        this._fxWidth = Math.round(a.width * 10) / 10;
        this._fxDistance = Math.round(a.distance * 10) / 10;
        this._fxBlur = Math.round(a.blur * 10) / 10;
        if (fx === 'shadow' && !this._fxAngle) this._fxAngle = 90;
        this.applyText();
      },
      setFxWidth: function (v) { this._fxWidth = Number(v); this.applyText(); },
      setFxAngle: function (v) { this._fxAngle = Number(v); this.applyText(); },
      setFxDistance: function (v) { this._fxDistance = Number(v); this.applyText(); },
      setFxBlur: function (v) { this._fxBlur = Number(v); this.applyText(); },
      // Effect colour: '' = automatic (contrast for outline, text colour for hollow, dark for shadow).
      pickFxColor: function (col) { this._txtFxColor = (col == null) ? '' : col; this.applyText(); },
      // Translate an effect name into Fabric stroke/fill/shadow props on a text object.
      // Each effect resets to a clean slate first so switching never stacks leftovers.
      // _txtFxColor overrides the effect's automatic colour when the customer picks one.
      _applyFx: function (o, fx, color, size) {
        if (!o) return;
        color = (typeof color === 'string' && color) ? color : '#16181b';
        size = size || 36;
        var fxCol = (typeof this._txtFxColor === 'string' && this._txtFxColor) ? this._txtFxColor : null;
        var contrast = this._isLight(color) ? '#16181b' : '#ffffff';
        var auto = this._fxAuto(size);
        var w = (this._fxWidth > 0) ? Number(this._fxWidth) : auto.width;
        var dist = (this._fxDistance >= 0) ? Number(this._fxDistance) : auto.distance;
        var blur = (this._fxBlur >= 0) ? Number(this._fxBlur) : auto.blur;
        // Angle is clockwise from 3 o'clock in SCREEN space, so +y points down.
        var rad = (Number(this._fxAngle) || 0) * Math.PI / 180;
        o.set({ fill: color, stroke: null, strokeWidth: 0, paintFirst: 'fill', shadow: null });
        if (fx === 'outline') {
          o.set({ fill: color, stroke: fxCol || contrast, strokeWidth: Math.max(0.5, w), paintFirst: 'stroke' });
        } else if (fx === 'hollow') {
          o.set({ fill: 'transparent', stroke: fxCol || color, strokeWidth: Math.max(0.5, w), paintFirst: 'stroke' });
        } else if (fx === 'shadow') {
          o.set({ shadow: new fabric.Shadow({
            color: fxCol ? this._rgba(fxCol, 0.5) : 'rgba(20,23,26,.38)',
            blur: Math.max(0, blur),
            offsetX: Math.round(Math.cos(rad) * dist * 100) / 100,
            offsetY: Math.round(Math.sin(rad) * dist * 100) / 100
          }) });
        }
        o.fxEffect = fx || 'none';
        o.fxColor = fxCol || '';
        // Persisted so reopening the editor or undoing restores the exact look.
        o.fxWidth = w; o.fxAngle = Number(this._fxAngle) || 0;
        o.fxDistance = dist; o.fxBlur = blur;
      },
      _rgba: function (hex, a) {
        var s = String(hex || '').trim().replace(/^#/, '');
        if (s.length === 3) s = s.split('').map(function (ch) { return ch + ch; }).join('');
        if (s.length !== 6) return 'rgba(20,23,26,' + a + ')';
        var r = parseInt(s.slice(0, 2), 16), g = parseInt(s.slice(2, 4), 16), b = parseInt(s.slice(4, 6), 16);
        return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
      },
      // Curve text along a symmetric arc (text-on-path). 0 = straight (no path). The arc
      // chord tracks the text's straight width, so it re-fits as the text/size changes.
      _applyCurve: function (o, curve) {
        curve = parseInt(curve, 10) || 0;
        o.fxCurve = curve;
        if (!curve) { if (o.path) o.set({ path: null }); return; }
        var w = (o.calcTextWidth ? o.calcTextWidth() : 0) || o.width || (o.fontSize * 4);
        w = Math.max(24, w);
        var sag = (Math.abs(curve) / 100) * w * 0.4;   // arc height from the chord
        if (sag < 1) { if (o.path) o.set({ path: null }); return; }
        var r = (w * w) / (8 * sag) + sag / 2;          // circle radius for chord w + sagitta
        var sweep = curve > 0 ? 1 : 0;
        var d = 'M ' + (-w / 2).toFixed(2) + ' 0 A ' + r.toFixed(2) + ' ' + r.toFixed(2) +
                ' 0 0 ' + sweep + ' ' + (w / 2).toFixed(2) + ' 0';
        o.set({ path: new fabric.Path(d, { fill: '', stroke: '' }), pathAlign: 'center', pathSide: 'left', pathStartOffset: 0 });
      },
      // Rebuild the arc after the customer edits the text characters (width changed).
      _recurveActive: function () {
        var c = window.__ftb2FabricCanvas; var o = c && c.getActiveObject();
        if (o && o.objType === 'text' && o.fxCurve) { this._applyCurve(o, o.fxCurve); c.requestRenderAll(); }
        this._pushHistoryDebounced();
      },

      // ==== Phase 36-03 — MVP shapes: exactly four tiles + fill / outline-only / thickness ====
      toggleShapeTiles: function () { this._shapeTilesOpen = !this._shapeTilesOpen; },
      // ＋ Shape — line (thin rounded Rect), circle, rectangle, rounded rectangle.
      // Stores baseFill + objType:'shape' so the panel + outline-only toggle survive save/restore.
      addShape: function (kind) {
        var c = window.__ftb2FabricCanvas; if (!c || typeof fabric === 'undefined') return;
        var cx = c.width / 2, cy = c.height / 2, fill = this._defaultInk(), o;
        if (kind === 'line') o = new fabric.Rect({ width: 170, height: 5, rx: 2.5, ry: 2.5, fill: fill });
        else if (kind === 'circle') o = new fabric.Circle({ radius: 55, fill: fill });
        else if (kind === 'rect') o = new fabric.Rect({ width: 150, height: 95, fill: fill });
        else o = new fabric.Rect({ width: 150, height: 95, rx: 16, ry: 16, fill: fill }); // 'rrect'
        o.set({ left: cx, top: cy, originX: 'center', originY: 'center', objType: 'shape', baseFill: fill });
        c.add(o); c.setActiveObject(o); c.requestRenderAll();   // object:added → one snapshot
        this._shapeTilesOpen = false; this.showPanelFor(o);
      },
      // Apply the shape panel controls: solid baseFill, or outline-only (transparent
      // fill + stroke in baseFill at the chosen thickness).
      applyShape: function () {
        var c = window.__ftb2FabricCanvas; var o = c && c.getActiveObject();
        if (!o || o.objType !== 'shape') return;
        o.baseFill = this._shpFill;
        if (this._shpOutlineOnly) {
          o.set({ fill: 'transparent', stroke: this._shpFill, strokeWidth: parseInt(this._shpThickness, 10) || 3 });
        } else { o.set({ fill: this._shpFill, stroke: null, strokeWidth: 0 }); }
        if (o.setCoords) o.setCoords();
        c.requestRenderAll(); this._pushHistoryDebounced();
      },
      pickShapeFill: function (col) { this._shpFill = col; this.applyShape(); },

      // ==== Phase 36-03 — selection-driven right panel + Duplicate/Delete + keyboard ====
      // Mobile ≤767 uses a segment bar (Background·Colors·Add·Style); desktop shows every
      // section at once and ignores _edSeg. Keep mobile-only state from leaking to desktop.
      _edIsMobile: function () {
        return !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
      },
      edSetSeg: function (s) {
        this._edSeg = s;
        if (s !== 'style') this._edSegPrev = s;
      },
      // Re-fit the Fabric canvas to the current center-column width, scaling the composition
      // proportionally so it is never letterboxed after a breakpoint cross (Fix Pack 04).
      _refitEditorCanvas: function () {
        var c = window.__ftb2FabricCanvas;
        var wrap = document.querySelector('.ftb2-ed2-canvas-wrap');
        if (!c || !wrap) return;
        var avail = wrap.clientWidth || 480;
        var w = Math.max(300, Math.min(560, avail - 36));
        var oldW = c.getWidth();
        if (!oldW || Math.abs(w - oldW) < 2) return;
        var ratio = w / oldW;
        c.setDimensions({ width: w, height: Math.round(w * 0.732) });
        c.getObjects().forEach(function (o) {
          o.set({
            scaleX: (o.scaleX || 1) * ratio, scaleY: (o.scaleY || 1) * ratio,
            left: (o.left || 0) * ratio, top: (o.top || 0) * ratio
          });
          o.setCoords();
        });
        c.requestRenderAll();
      },
      // Empty state by default; text panel for objType 'text' (syncs font/size/colour/
      // outline); shape panel for objType 'shape' (syncs fill/outline-only/thickness).
      // The locked base logo (isBase) never opens a panel.
      showPanelFor: function (o) {
        var c = window.__ftb2FabricCanvas;
        if (o === undefined) o = c ? c.getActiveObject() : null;
        if (!o || o.isBase) {
          this._selHasObj = false; this._selType = '';
          // Mobile: leaving a selection drops out of the Style segment.
          if (this._edIsMobile() && this._edSeg === 'style') this._edSeg = this._edSegPrev || 'background';
          return;
        }
        var isText = (o.type === 'i-text' || o.type === 'text' || o.type === 'textbox');
        this._selType = isText ? 'text' : (o.type === 'image' ? 'image' : 'shape');
        this._selHasObj = true;
        // Mobile: selecting an object auto-activates the Style segment (desktop ignores _edSeg).
        if (this._edIsMobile()) this._edSeg = 'style';
        if (isText) {
          this._txtFont = o.fontFamily || "'Archivo Black', sans-serif";
          this._txtSize = Math.round(o.fontSize || 36);
          // Effect (Fix Pack 05 P1) — prefer the stored fxEffect; infer for legacy objects.
          var fx = o.fxEffect;
          if (!fx) {
            if (o.shadow) fx = 'shadow';
            else if (o.stroke && o.fill === 'transparent') fx = 'hollow';
            else if (o.stroke) fx = 'outline';
            else fx = 'none';
          }
          this._txtEffect = fx;
          // Restore the tunables from the object so the sliders match what is on
          // screen; fall back to the size-derived preset for older objects.
          var _fa = this._fxAuto(Math.round(o.fontSize || 36));
          this._fxWidth = (typeof o.fxWidth === 'number') ? o.fxWidth : Math.round(_fa.width * 10) / 10;
          this._fxAngle = (typeof o.fxAngle === 'number') ? o.fxAngle : 90;
          this._fxDistance = (typeof o.fxDistance === 'number') ? o.fxDistance : Math.round(_fa.distance * 10) / 10;
          this._fxBlur = (typeof o.fxBlur === 'number') ? o.fxBlur : Math.round(_fa.blur * 10) / 10;
          // Hollow keeps the colour in the stroke; every other effect keeps it in the fill.
          this._txtColor = (fx === 'hollow' && typeof o.stroke === 'string') ? o.stroke
            : ((typeof o.fill === 'string' && o.fill !== 'transparent') ? o.fill : '#16181b');
          this._txtFxColor = (typeof o.fxColor === 'string') ? o.fxColor : '';
          this._txtSpacing = o.charSpacing || 0;
          this._txtCurve = o.fxCurve || 0;
        } else if (this._selType === 'shape') {
          this._shpFill = o.baseFill || ((typeof o.fill === 'string' && o.fill !== 'transparent') ? o.fill : '#16181b');
          this._shpOutlineOnly = (o.fill === 'transparent' || o.fill == null) && (o.strokeWidth || 0) > 0;
          this._shpThickness = o.strokeWidth || 3;
        }
      },
      // Duplicate the active object offset ~22px, preserving objType + baseFill.
      duplicateObj: function () {
        var c = window.__ftb2FabricCanvas; var o = c && c.getActiveObject(); var self = this;
        if (!o || o.isBase || !o.clone) return;
        o.clone(['objType', 'baseFill']).then(function (cl) {
          cl.set({ left: (o.left || 0) + 22, top: (o.top || 0) + 22 });
          cl.objType = o.objType; if (o.baseFill != null) cl.baseFill = o.baseFill;
          c.add(cl); c.setActiveObject(cl); c.requestRenderAll();   // object:added → snapshot
          self.showPanelFor(cl);
        });
      },
      // Delete the active object and return to the empty-state panel.
      deleteObj: function () {
        var c = window.__ftb2FabricCanvas; var o = c && c.getActiveObject();
        if (!o || o.isBase) return;
        c.remove(o); c.discardActiveObject(); c.requestRenderAll();  // object:removed → snapshot
        this.showPanelFor(null);
      },
      // Desktop keyboard: Delete/Backspace removes the active object (never while a
      // text object is being edited, and never when typing in a field); Ctrl/Cmd+Z
      // undo, +Shift redo. Guarded so it only acts while the editor drawer is open.
      _editorKeydown: function (e) {
        if (!this._editorOpen) return;
        var t = (e.target && e.target.tagName) || '';
        if (t === 'INPUT' || t === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return;
        var c = window.__ftb2FabricCanvas; if (!c) return;
        var o = c.getActiveObject();
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (o && !o.isBase && !o.isEditing) { e.preventDefault(); this.deleteObj(); }
        } else if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) {
          e.preventDefault(); if (e.shiftKey) this.edRedo(); else this.edUndo();
        }
      },


      // ---- undo / redo — JSON snapshots (max 30), index-pointer model ----
      // Custom keys keep our metadata across serialize/restore so the base layer
      // stays identifiable + re-lockable. A restoring flag prevents feedback loops
      // (loadFromJSON fires object:added, which must NOT push new snapshots).
      _HIST_KEYS: ['objType', 'baseFill', 'isBase', 'selectable', 'evented', 'fxEffect', 'fxColor', 'fxCurve', 'fxWidth', 'fxAngle', 'fxDistance', 'fxBlur', 'pathAlign', 'pathSide', 'pathStartOffset'],
      _pushHistory: function () {
        var c = window.__ftb2FabricCanvas; var H = window.__ftb2Hist;
        if (!c || !H || H.restoring) return;
        var json;
        try { json = JSON.stringify(c.toJSON(this._HIST_KEYS)); } catch (e) { return; }
        H.stack = H.stack.slice(0, H.idx + 1);
        H.stack.push(json);
        if (H.stack.length > 30) H.stack.shift();
        H.idx = H.stack.length - 1;
        this._syncHistoryFlags();
      },
      _syncHistoryFlags: function () {
        var H = window.__ftb2Hist || { stack: [], idx: -1 };
        this._canUndo = H.idx > 0;
        this._canRedo = H.idx < H.stack.length - 1;
      },
      _restoreHistory: function (json) {
        var c = window.__ftb2FabricCanvas; var H = window.__ftb2Hist; var self = this;
        if (!c || !H) return;
        H.restoring = true;
        c.loadFromJSON(JSON.parse(json)).then(function () {
          // Re-locate + re-lock the base logo by its isBase marker after every restore.
          var base = c.getObjects().find(function (o) { return o.isBase; }) || null;
          if (base) { base.set({ selectable: false, evented: false }); c.sendObjectToBack(base); }
          window.__ftb2BaseLogo = base;
          c.discardActiveObject();
          c.requestRenderAll();
          self._selHasObj = false; self._selType = '';
          H.restoring = false;
          self._syncHistoryFlags();
        }).catch(function () { H.restoring = false; });
      },
      edUndo: function () {
        var H = window.__ftb2Hist; if (!H || H.idx <= 0) return;
        H.idx--; this._restoreHistory(H.stack[H.idx]); this._syncHistoryFlags();
      },
      edRedo: function () {
        var H = window.__ftb2Hist; if (!H || H.idx >= H.stack.length - 1) return;
        H.idx++; this._restoreHistory(H.stack[H.idx]); this._syncHistoryFlags();
      },

      // Flatten the canvas to a PNG, upload it, then ask where it should apply.
      // The flattened image runs back through the SAME version pipeline via
      // applyScope → _generateVersions.
      // Trim the exported PNG down to its visible artwork.
      //
      // canvas.toDataURL() exports the WHOLE editor canvas, and the composition
      // usually occupies only the middle of it. That transparent margin is baked
      // into the saved PNG, so when the logo is later fitted into a print zone the
      // zone fits the MARGIN — the artwork itself ends up noticeably smaller than
      // the area the customer paid to decorate.
      //
      // The content bounds come from forteeContentBox.analyzeContent, the same
      // function that crops every other logo surface, and just that region is
      // re-exported. Resolves { dataUrl, rect } — rect is the kept region in
      // exported pixels, so the edits-only layer can be cropped to EXACTLY the
      // same box and overlay the production PNG 1:1. On any failure (blank
      // canvas, a tainted context, the module missing) it resolves the ORIGINAL
      // data URL and the full frame, so saving can never be blocked by cropping.
      _cropDataUrlToContent: function (dataUrl) {
        return new Promise(function (resolveRaw) {
          var w = 0, h = 0;
          function resolve(out, rect) {
            resolveRaw({ dataUrl: out, rect: rect || { x: 0, y: 0, w: w, h: h } });
          }
          try {
            var img = new Image();
            img.onload = function () {
              try {
                w = img.naturalWidth || img.width; h = img.naturalHeight || img.height;
                if (!w || !h) return resolve(dataUrl);
                var cv = document.createElement('canvas');
                cv.width = w; cv.height = h;
                var ctx = cv.getContext('2d');
                ctx.drawImage(img, 0, 0);
                var cb = window.forteeContentBox;
                if (!cb) {
                  console.error('[FORTEE editor] fortee-content-box.js not loaded — design saved uncropped');
                  return resolve(dataUrl);
                }
                // No padding: the print zone fits whatever this returns, so any
                // margin comes straight off the printed logo's size.
                var box = cb.analyzeContent(w, h, ctx.getImageData(0, 0, w, h).data).box;
                if (!box) return resolve(dataUrl);   // empty, already tight, or unreadable
                var out = document.createElement('canvas');
                out.width = box.w; out.height = box.h;
                out.getContext('2d').drawImage(cv, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
                resolve(out.toDataURL('image/png'), { x: box.x, y: box.y, w: box.w, h: box.h });
              } catch (e) { resolve(dataUrl); }
            };
            img.onerror = function () { resolve(dataUrl); };
            img.src = dataUrl;
          } catch (e) { resolve(dataUrl); }
        });
      },
      // Crop a data URL to an exact pixel rect (the one _cropDataUrlToContent
      // returned for the composite). Resolves the input unchanged on failure or
      // when the rect is the whole frame.
      _cropDataUrlToRect: function (dataUrl, rect) {
        return new Promise(function (resolve) {
          if (!rect || !(rect.w > 0 && rect.h > 0)) return resolve(dataUrl);
          try {
            var img = new Image();
            img.onload = function () {
              try {
                var w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
                if (!w || !h) return resolve(dataUrl);
                if (rect.x === 0 && rect.y === 0 && rect.w >= w && rect.h >= h) return resolve(dataUrl);
                var out = document.createElement('canvas');
                out.width = rect.w; out.height = rect.h;
                out.getContext('2d').drawImage(img, rect.x, rect.y, rect.w, rect.h, 0, 0, rect.w, rect.h);
                resolve(out.toDataURL('image/png'));
              } catch (e) { resolve(dataUrl); }
            };
            img.onerror = function () { resolve(dataUrl); };
            img.src = dataUrl;
          } catch (e) { resolve(dataUrl); }
        });
      },

      // A compact ENGLISH description of the customer's editor objects, for
      // production to redo them on the original artwork. Positions are object
      // centres as 0-1 fractions of `rect` (the crop box of the production PNG,
      // in exported pixels); `multiplier` converts canvas units to those pixels.
      // Never built from _t(): it lands on the order. Always <= 255 chars.
      _editsSpec: function (objects, rect, multiplier) {
        var LIMIT = 255;
        var m = Number(multiplier) || 1;
        var rw = (rect && rect.w > 0) ? rect.w : 1, rh = (rect && rect.h > 0) ? rect.h : 1;
        var rx = (rect && rect.x) || 0, ry = (rect && rect.y) || 0;
        function frac(n) {
          var s = (Math.round(n * 100) / 100).toFixed(2);
          return s.replace(/^(-?)0\./, '$1.');
        }
        function num(n) { return String(Math.round((Number(n) || 0) * 10) / 10); }
        function colour(c) { return (typeof c === 'string' && c) ? c : 'custom'; }
        function clean(s) {
          s = String(s == null ? '' : s).replace(/[\u0000-\u001f\u007f]+/g, ' ')
            .replace(/"/g, "'").replace(/\s+/g, ' ').trim();
          return s.length > 40 ? s.slice(0, 37) + '...' : s;
        }
        function centre(o) {
          var p = (typeof o.getCenterPoint === 'function') ? o.getCenterPoint() : { x: o.left, y: o.top };
          return '@' + frac(((Number(p.x) || 0) * m - rx) / rw) + ',' + frac(((Number(p.y) || 0) * m - ry) / rh);
        }
        function describe(o) {
          var type = String(o.type || '').toLowerCase();
          var isText = o.objType === 'text' || /^(text|i-?text|textbox)$/.test(type);
          if (isText) {
            var font = String(o.fontFamily || '').split(',')[0].replace(/['"]/g, '').trim() || 'default';
            var parts = ['text "' + clean(o.text) + '"', font, colour(o.fill),
              'h' + frac((Number(o.fontSize) || 0) * (Number(o.scaleY) || 1) * m / rh)];
            var fx = o.fxEffect;
            if (fx === 'outline' || fx === 'hollow') parts.push(fx + ' ' + colour(o.stroke) + ' w' + num(o.strokeWidth));
            else if (fx === 'shadow') parts.push('shadow ' + colour(o.shadow && o.shadow.color));
            if (Number(o.charSpacing)) parts.push('spacing' + num(o.charSpacing));
            if (Number(o.fxCurve)) parts.push('curve' + num(o.fxCurve));
            parts.push(centre(o));
            return parts.join(' ');
          }
          var sw = (typeof o.getScaledWidth === 'function') ? o.getScaledWidth() : (Number(o.width) || 0) * (Number(o.scaleX) || 1);
          var sh = (typeof o.getScaledHeight === 'function') ? o.getScaledHeight() : (Number(o.height) || 0) * (Number(o.scaleY) || 1);
          var kind;
          if (type === 'circle') kind = 'circle';
          else if (Number(o.rx) > 0 && (Number(o.height) || 0) * (Number(o.scaleY) || 1) <= 8) kind = 'line';
          else if (Number(o.rx) > 0) kind = 'rrect';
          else kind = 'rect';
          var paint = (o.fill === 'transparent' && Number(o.strokeWidth) > 0)
            ? 'outline ' + colour(o.stroke) + ' t' + num(o.strokeWidth)
            : 'fill ' + colour(typeof o.fill === 'string' ? (o.baseFill || o.fill) : o.fill);
          return kind + ' ' + paint + ' w' + frac(sw * m / rw) + ' h' + frac(sh * m / rh) + ' ' + centre(o);
        }
        var items = (objects || []).filter(function (o) { return o && !o.isBase; }).map(describe);
        var all = items.join('; ');
        if (all.length <= LIMIT) return all;
        var out = '', used = 0;
        for (var i = 0; i < items.length; i++) {
          var next = out ? out + '; ' + items[i] : items[i];
          var suffix = '; +' + (items.length - i - 1) + ' more';
          if (next.length + suffix.length > LIMIT) break;
          out = next; used = i + 1;
        }
        var rest = '+' + (items.length - used) + ' more';
        return out ? out + '; ' + rest : rest;
      },

      editorSaveEdit: function () {
        var self = this;
        var canvas = window.__ftb2FabricCanvas;
        if (!canvas) return Promise.resolve();
        canvas.discardActiveObject(); canvas.requestRenderAll();
        var dataUrl, editorJSON, edits;
        try {
          dataUrl = canvas.toDataURL({ format: 'png', multiplier: 3 });
          editorJSON = JSON.stringify(canvas.toJSON(this._HIST_KEYS));
          edits = canvas.getObjects().filter(function (o) { return !o.isBase; });
        } catch (e) { this.showToast(this._t('artwork_save_failed', 'Could not save — please try again.'), false); return Promise.resolve(); }
        // The edits-only copy: the same export with the logo hidden, so
        // production can overlay the customer's text and shapes on the
        // ORIGINAL artwork. Only when there is something to copy.
        var editsDataUrl = null;
        if (edits.length) {
          var base = canvas.getObjects().find(function (o) { return o.isBase; }) || null;
          try {
            if (base) base.visible = false;
            editsDataUrl = canvas.toDataURL({ format: 'png', multiplier: 3 });
          } catch (e) {
            console.error('[FORTEE editor] edits layer export failed — the design is saved without its edits copy', e);
            editsDataUrl = null;
          } finally {
            if (base) base.visible = true;
            canvas.requestRenderAll();
          }
        }
        // The composition JSON travels with the scoped artwork so reopening the
        // editor rehydrates editable objects (applyScope → groupArtwork[view].editorJSON).
        this._scopePendingEditorJSON = editorJSON;
        this._scopePendingEdits = null;
        var pid = this._fabricProductId;
        var pubkey = (window.bundleData && window.bundleData.uploadcarePubkey) ||
          (window.forteeShared && window.forteeShared._uploadcarePubkey) || '';
        function upload(d) {
          return (window.forteeShared && window.forteeShared.uploadToUploadcare)
            ? window.forteeShared.uploadToUploadcare(d, pubkey) : Promise.resolve(null);
        }
        this.showToast(this._t('artwork_saving', 'Saving your design…'), false);
        // Trim the transparent margin before upload so the saved logo fills its
        // print zone instead of the zone fitting the empty canvas around it.
        return self._cropDataUrlToContent(dataUrl).then(function (crop) {
          var spec = edits.length ? self._editsSpec(edits, crop.rect, 3) : '';
          var layer = !editsDataUrl ? Promise.resolve(null)
            : self._cropDataUrlToRect(editsDataUrl, crop.rect).then(upload).catch(function (e) {
              console.error('[FORTEE editor] edits layer upload failed for product ' + pid, e);
              return null;
            });
          return Promise.all([upload(crop.dataUrl), layer]).then(function (res) {
            var url = res[0], layerUrl = res[1];
            self.closeEditor();
            if (!url) { self.showToast(self._t('artwork_save_failed', 'Could not save — please try again.'), false); return; }
            if (edits.length) {
              var layerOk = typeof layerUrl === 'string' && /^https:\/\//.test(layerUrl);
              if (editsDataUrl && !layerOk) {
                console.error('[FORTEE editor] edits layer upload returned no https url for product ' + pid +
                  ' — the design is saved, the order will carry the edit spec without the edits layer');
              }
              self._scopePendingEdits = { editsLayerUrl: layerOk ? layerUrl : null, editsSpec: spec || null };
            }
            self._openScope(pid, url);
          });
        }).catch(function () {
          self.closeEditor();
          self.showToast(self._t('artwork_save_failed', 'Could not save — please try again.'), false);
        });
      },

      // ---- Upload a new logo (Path A): upload → prep → scope ----
      // Routes the upload to a scratch group key so it doesn't disturb existing
      // per-view artwork until the customer confirms a scope in _openScope.
      startUploadNew: function (productId) {
        this._artworkEditFor = null;
        this._editorOpen = false;
        this._uploadNewProductId = productId;
        this._pendingUploadIsNew = true;
        this.triggerUpload(NEW_LOGO_KEY);
      },

      // ---- Scope selector (§3.4): where should this logo apply? ----
      // sourceRecord is passed ONLY by the new-upload path (the pipeline's
      // _prepMaybeDone). An editor save calls with two arguments.
      _openScope: function (productId, newLogoUrl, sourceRecord) {
        this._pendingScopeProductId = productId;
        this._scopePendingUrl = newLogoUrl;
        this._scopePendingSource = sourceRecord ? Object.assign({}, sourceRecord) : null;
        var view = this._editorView || this._activeViewForProduct(productId) || '';
        var choice = view.indexOf('cap') !== -1 ? 'cap' : (view.indexOf('back') !== -1 ? 'back' : 'front');
        // The default has to be a choice the dialog will actually offer, or the
        // customer opens onto a pre-selected option that is hidden and "Apply
        // to <label>" does nothing. 'this' is the backstop: sleeves match no
        // named option, so on a garment decorated only on a sleeve every other
        // choice is empty.
        if (!this._scopeHasOption(choice)) {
          var order = ['this', 'front', 'back', 'cap', 'all'];
          for (var i = 0; i < order.length; i++) {
            if (this._scopeHasOption(order[i])) { choice = order[i]; break; }
          }
        }
        this._scopeChoice = choice;
        this._scopeOpen = true;
      },
      setScope: function (choice) { this._scopeChoice = choice; },
      closeScope: function () {
        this._scopeOpen = false; this._scopePendingUrl = null; this._scopePendingEditorJSON = null;
        this._scopePendingSource = null; this._scopePendingEdits = null;
      },

      _viewMatchesScope: function (view, choice) {
        if (choice === 'all') return true;
        // Just the placement being edited. The named options below cover front,
        // back and caps only — SLEEVES match none of them, so before this
        // existed the sole route to a sleeve logo was "Apply to all", which is
        // exactly the option that used to decorate (and not charge for) every
        // other side as well.
        if (choice === 'this') return view === (this._editorView || this._activeViewForProduct(null));
        if (choice === 'cap') return view.indexOf('cap') !== -1;
        if (choice === 'front') return view.indexOf('front') !== -1 && view.indexOf('cap') === -1;
        if (choice === 'back') return view.indexOf('back') !== -1;
        return false;
      },

      // The views this scope may write to.
      //
      // ONLY views that ALREADY hold artwork. This dialog appears after an edit
      // or a "upload a new logo" replace, so its job is to say which of the
      // customer's EXISTING logos get swapped — it must never turn an
      // undecorated side into a decorated one.
      //
      // It used to iterate Object.keys(groupArtwork) raw, and that is a pricing
      // hole rather than a cosmetic one. The two stores are independent:
      // groupArtwork/perViewArtwork decides where a logo prints, while
      // selectedDecorations -> getDecorationTierName -> the Shopify variant
      // decides what it costs. Every other write path in the single builder
      // (reuseExistingLogo, reuseSpecificLogo, the upload path) registers a
      // placement alongside the artwork so the tier moves with it; this one
      // never did. The single builder also seeds perViewArtwork with all four
      // keys up front (single-builder.js: { front, back, left, right }), so
      // "Apply to all" matched every side of a tee whether or not it was
      // configured — four decorations shipped to production on a line priced
      // for one.
      //
      // Adding a decoration stays where it has always been: the placement
      // picker, which prices it.
      _scopeEligibleViews: function (choice) {
        var store = this.groupArtwork || {};
        var self = this;
        return Object.keys(store).filter(function (view) {
          if (view === NEW_LOGO_KEY) return false;
          var a = store[view];
          if (!a || !(a.url || a.bgRemovedUrl || a.originalUrl)) return false;
          return self._viewMatchesScope(view, choice);
        });
      },

      // Does this scope option match anything? Drives x-show on the buttons, so
      // a tee never offers "Caps" and "Apply to all" only shows when there is
      // genuinely more than one decorated side.
      _scopeHasOption: function (choice) {
        var eligible = this._scopeEligibleViews(choice);
        if (choice === 'all') return eligible.length > 1;
        if (choice === 'this') {
          // Only worth offering when no named option already means exactly this
          // one view — otherwise the dialog shows two buttons that do the same
          // thing (a plain tee decorated on the front would get both "This
          // placement only" and "All front placements").
          if (eligible.length !== 1) return false;
          var view = eligible[0];
          var named = ['front', 'back', 'cap'];
          for (var i = 0; i < named.length; i++) {
            var set = this._scopeEligibleViews(named[i]);
            if (set.length === 1 && set[0] === view) return false;
          }
          return true;
        }
        return eligible.length > 0;
      },

      applyScope: function () {
        var self = this;
        var url = this._scopePendingUrl;
        if (!url) { this.closeScope(); return; }
        var choice = this._scopeChoice;
        var source = this._scopePendingSource;
        var edits = this._scopePendingEdits || {};
        var newArt = { url: url, originalUrl: url, bgRemovedUrl: url, originalCroppedUrl: null, bgRemovedCroppedUrl: null, filename: 'edited-logo.png', editorJSON: self._scopePendingEditorJSON || null,
                       editsLayerUrl: edits.editsLayerUrl || null, editsSpec: edits.editsSpec || null };
        // Views that already hold a logo, never the empty ones — see
        // _scopeEligibleViews for why writing to an undecorated side is a
        // pricing bug and not a convenience.
        var targets = this._scopeEligibleViews(choice);
        if (!targets.length) {
          this.closeScope();
          this.showToast(this._t('artwork_scope_none', 'Nothing to apply to — add the placement first.'), false);
          return;
        }
        targets.forEach(function (view) {
          if (source) {
            // NEW UPLOAD. The customer replaced the logo, they did not edit it,
            // so nothing of the previous logo may survive on the record: its
            // url as editedFrom is exactly what made order 7838 print the old
            // bundle logo as the original. The raw upload is the original and
            // the cutout (null until removal lands) is production.
            self.groupArtwork[view] = Object.assign({}, source, {
              originalUrl: source.originalUrl || source.url,
              editedFrom: null, editorJSON: null, editsLayerUrl: null, editsSpec: null
            });
            self._generateVersions(view);
            return;
          }
          // Remember the logo this edit was made FROM. Without it the pre-edit
          // upload is discarded here and can never be offered again — the artwork
          // library derives entirely from this store. Chained through repeated
          // edits so it always points at the TRUE original upload, not the
          // previous edit.
          var prev = self.groupArtwork[view];
          var editedFrom = prev ? (prev.editedFrom || prev.originalUrl || prev.url || null) : null;
          self.groupArtwork[view] = Object.assign({}, newArt, { editedFrom: editedFrom });
          self._generateVersions(view);
        });
        this._mockup2Cache = {};
        this._buildPrePlacedTiles();
        this.closeScope();
        this.showToast(this._t('artwork_applied', 'Your changes are live.'), true);
      },

      _scopeLabel: function (choice) {
        return { all: this._t('scope_all', 'all placements'), front: this._t('scope_front', 'all front placements'),
                 back: this._t('scope_back', 'all back placements'), cap: this._t('scope_cap', 'caps'),
                 'this': this._t('scope_this', 'this placement') }[choice] || choice;
      },
    };
  };
})();
